import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { sharedStyles } from "@lib/sharedStyles";

// Ensure custom elements are registered
import "@/components/button/BasicButton";

const API_BASE = (window.location.port === '5173') ? 'http://localhost:8002' : '';

function navigate(path: string) {
  const state = {};
  window.history.pushState(state, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

interface QuizListItem {
  id: string;
  name: string | null;
  quiz_type?: { id: string; name: string } | null;
  question_count: number;
  team_count: number;
  created_at?: string | null;
}

@customElement('my-quizzes-page')
export default class MyQuizzesPage extends LitElement {
  static styles = [sharedStyles, css`
    :host { display: block; padding: 1rem; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ddd; padding: 0.5rem; text-align: left; }
    th { background: #f7f7f7; }
    .actions { white-space: nowrap; display: flex; gap: 0.5rem; }
    button { padding: 0.35rem 0.6rem; border-radius: 6px; border: 1px solid #ccc; cursor: pointer; }
  `];

  @state() private loading = true;
  @state() private error: string | null = null;
  @state() private items: QuizListItem[] = [];

  connectedCallback(): void {
    super.connectedCallback();
    this.load();
  }

  async load() {
    this.loading = true;
    this.error = null;
    try {
      const res = await fetch(`${API_BASE}/api/quizzes`);
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      const data = await res.json();
      this.items = data.results ?? [];
    } catch (e: any) {
      this.error = e?.message ?? 'Failed to load';
    } finally {
      this.loading = false;
    }
  }

  render() {
    if (this.loading) return html`<div>Loading...</div>`;
    if (this.error) return html`<div class="error">${this.error}</div>`;
    return html`
      <div style="display:flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem;">
        <h2>My Quizzes</h2>
        <basic-button @click=${() => navigate('/')}>Back to Main Menu</basic-button>
      </div>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Questions</th>
            <th>Teams</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${this.items.map(q => html`
            <tr>
              <td>${q.name ?? '(unnamed)'}</td>
              <td>${q.quiz_type?.name ?? '-'}</td>
              <td>${q.question_count}</td>
              <td>${q.team_count}</td>
              <td>${q.created_at ? new Date(q.created_at).toLocaleString() : '-'}</td>
              <td class="actions">
                <basic-button @click=${() => navigate(`/quiz/create/${q.id}`)}>Edit</basic-button>
                <basic-button @click=${() => navigate(`/quiz/play/${q.id}`)}>Start</basic-button>
              </td>
            </tr>
          `)}
        </tbody>
      </table>
    `;
  }
}
